import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Modal } from 'antd';
import { studentService } from '../../services/studentService';
import { authService, ROLE_LEVELS } from '../../services/authService';
import AV from '../../config/leancloud';

const StudentVerification = () => {
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [unverifiedStudents, setUnverifiedStudents] = useState([]);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const canAccess = await authService.canAccess(ROLE_LEVELS.FINANCE);
        setHasPermission(canAccess);
      } catch (error) {
        console.error('Permission check error:', error);
        message.error('权限检查失败');
      }
    };
    checkPermission();
  }, []);

  const fetchUnverifiedStudents = async () => {
    try {
      setLoading(true);
      console.log('Fetching unverified students...');
      const students = await studentService.getUnverifiedStudents();
      console.log('Fetched students:', students);
      setUnverifiedStudents(students);
    } catch (error) {
      console.error('Error fetching unverified students:', error);
      message.error('获取未审核学员失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnverifiedStudents();
  }, []);

  const handleVerification = async (verified) => {
    if (selectedRows.length === 0) {
      message.warning('请选择要审核的学员');
      return;
    }

    Modal.confirm({
      title: `确认${verified ? '通过' : '不通过'}所选学员的审核？`,
      content: `将${verified ? '通过' : '不通过'} ${selectedRows.length} 名学员的审核`,
      onOk: async () => {
        try {
          setLoading(true);
          
          // Process each student verification
          await Promise.all(
            selectedRows.map(async student => {
              // Update student verification status
              await studentService.updateStudent(student.objectId, {
                verified,
                verifiedAt: new Date().toISOString(),
                notes: undefined
              });

              // If verified, create payment record
              if (verified) {
                // Student verification is successful
                // No need to create payment records as we track income directly from student tuition fees
                console.log(`Student ${student.name} verified successfully.`);
              }
            })
          );

          message.success('审核操作成功');
          fetchUnverifiedStudents();
          setSelectedRows([]);
        } catch (error) {
          console.error('Verification error:', error);
          message.error('审核操作失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
    },
    {
      title: '学费',
      dataIndex: 'tuitionFee',
      key: 'tuitionFee',
    },
    {
      title: '收据号',
      dataIndex: 'receiptNumber',
      key: 'receiptNumber',
    },
    {
      title: '收据照片',
      key: 'receiptPic',
      render: (_, record) => (
        record.receiptPic ? 
          <a href={record.receiptPic} target="_blank" rel="noopener noreferrer">
            查看收据
          </a> : 
          '无'
      )
    },
    {
      title: '学费收据',
      key: 'tuitionReceipt',
      render: (_, record) => (
        record.tuitionReceipt ? 
          <a href={record.tuitionReceipt} target="_blank" rel="noopener noreferrer">
            查看收据
          </a> : 
          '无'
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedRows.map(row => row.objectId),
    onChange: (_, rows) => setSelectedRows(rows),
    columnWidth: 60,
    fixed: true
  };

  const onRow = (record) => ({
    onClick: () => {
      const isSelected = selectedRows.some(row => row.objectId === record.objectId);
      if (isSelected) {
        setSelectedRows(selectedRows.filter(row => row.objectId !== record.objectId));
      } else {
        setSelectedRows([...selectedRows, record]);
      }
    },
    style: {
      cursor: 'pointer',
      background: selectedRows.some(row => row.objectId === record.objectId) 
        ? '#e6f7ff' 
        : undefined
    }
  });

  if (!hasPermission) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px', 
        fontSize: '16px', 
        color: '#ff4d4f' 
      }}>
        您没有权限访问此页面，请联系管理员
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          onClick={() => handleVerification(true)}
          disabled={selectedRows.length === 0}
        >
          审核通过
        </Button>
        <Button 
          danger 
          onClick={() => handleVerification(false)}
          disabled={selectedRows.length === 0}
        >
          审核不通过
        </Button>
      </Space>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={unverifiedStudents}
        loading={loading}
        rowKey="objectId"
        onRow={onRow}
      />
    </div>
  );
};

export default StudentVerification; 